// runAsTrail — pragmatisches Wrapper-Utility, das eine CachedRun in eine
// CachedTrail-kompatible Struktur überführt. So kann die MapPage den Run ohne
// Spezial-Pfad wie einen normalen Trail rendern (Aura, ElevationProfile,
// RIDE-Button, Navigate-to-start, CruxToggle).
//
// Warum "pseudo-Trail" statt echter zweiter Selected-State?
//   - Die MapPage ist schon dicht verkabelt um `selected: CachedTrail`. Ein
//     parallele `selectedRun: CachedRun` würde 20+ Code-Pfade verdoppeln
//     (Aura-Layer, ElevationProfile, Progress, Ride-Mode-Strip, Navigate-to-
//     start). Der Wrapper ist ~100 Zeilen und halt wirklich nur ein Cast —
//     alle downstream-Konsumenten sind happy.
//   - Einzige Stellen, die wirklich unterscheiden müssen: die drei Buttons die
//     für Runs keinen Sinn haben (AddCruxHere → Cruxes leben auf Original-
//     Trails; RunsForTrailButton → "welche Runs enthalten diesen Run?" ist
//     nicht meaningful). Wir erkennen das per ID-Prefix `run:`.
//
// ID-Prefix `run:` ist Vertrag: echte Trail-IDs sind UUIDs ohne Doppelpunkte,
// der Prefix kollidiert also garantiert nicht. In CruxPoint-Callbacks u. ä.
// benutzen wir das auch als Flag "bitte nix schreiben".

import type { CruxPoint, Difficulty, GpxPoint, GpxTrack } from '@adtu/shared';
import { trackLength, elevationStats, DIFFICULTY_COLOR } from '@adtu/shared';
import type { CachedRun } from './runs';
import type { CachedTrail } from './trails';
import { cumulativeDistances, nearestPointOnTrack } from './trailGeom';
import type { ElevationSegment } from './routeSegmentMap';

export const RUN_ID_PREFIX = 'run:';

/**
 * Ist die `selected`-ID ein Pseudo-Run?
 */
export function isRunPseudoId(id: string | undefined | null): boolean {
  return typeof id === 'string' && id.startsWith(RUN_ID_PREFIX);
}

/**
 * Extrahiert die Original-Run-ID aus dem Pseudo-Trail-ID. Leer-String wenn's
 * gar kein Pseudo-Run ist (defensive).
 */
export function extractRunId(pseudoId: string): string {
  if (!isRunPseudoId(pseudoId)) return '';
  return pseudoId.slice(RUN_ID_PREFIX.length);
}

/**
 * Interpoliert lat/lon am gegebenen distanceMeters entlang eines Tracks.
 * Benutzt eine bereits-berechnete cumulative-Distances-Liste (Fast-Path,
 * wir machen das hier mehrfach pro Trail).
 */
function pointAtDistance(
  points: GpxPoint[],
  cum: number[],
  distanceMeters: number,
): { lat: number; lon: number } | null {
  if (points.length === 0 || cum.length === 0) return null;
  if (distanceMeters <= 0) return { lat: points[0]!.lat, lon: points[0]!.lon };
  const last = points[points.length - 1]!;
  const totalLen = cum[cum.length - 1] ?? 0;
  if (distanceMeters >= totalLen) return { lat: last.lat, lon: last.lon };
  // Binary wäre schneller, aber bei <2000 Punkten pro Trail ist linear ok.
  let i = 0;
  while (i < cum.length - 1 && (cum[i + 1] ?? 0) < distanceMeters) i++;
  const a = points[i]!;
  const b = points[Math.min(i + 1, points.length - 1)]!;
  const dStart = cum[i] ?? 0;
  const dEnd = cum[Math.min(i + 1, cum.length - 1)] ?? dStart;
  const segLen = dEnd - dStart;
  const t = segLen > 0 ? (distanceMeters - dStart) / segLen : 0;
  return {
    lat: a.lat + (b.lat - a.lat) * t,
    lon: a.lon + (b.lon - a.lon) * t,
  };
}

/**
 * Aggregiert die Cruxes aus allen im Run enthaltenen Trails und projiziert
 * sie auf die Run-GPX. Für jede Crux auf Trail T bei distanceMeters:
 *   1) Finde (lat,lon) an dieser Stelle auf T's Track.
 *   2) Projiziere (lat,lon) auf Run's Track → alongTrackMeters auf dem Run.
 *   3) Nimm Elevation vom Original-Crux (wenn vorhanden).
 *
 * Runs und Trails haben meist überlappende GPX-Stücke, der Fußpunkt ist also
 * verlässlich. CruxPoint hat selbst kein off-track-Feld, wir benutzen also
 * nur alongTrackMeters vom Projektions-Ergebnis.
 *
 * Dedupliziert NICHT: wenn zwei Trails denselben physischen Crux-Punkt
 * enthalten (unwahrscheinlich), zeigen wir ihn zwei Mal. Das ist im Zweifel
 * das sichere Verhalten.
 *
 * Sortiert das Ergebnis nach distanceMeters entlang Run aufsteigend —
 * ElevationProfile geht davon aus dass die Liste in Reihenfolge ist.
 */
export function aggregateCruxesOntoRun(
  run: CachedRun,
  trailsById: Map<string, CachedTrail>,
): CruxPoint[] {
  const runPoints = run.track.points;
  if (runPoints.length < 2) return [];
  const runCum = cumulativeDistances(runPoints);
  const out: CruxPoint[] = [];

  for (const trailId of run.trailIds) {
    const trail = trailsById.get(trailId);
    if (!trail) continue;
    const tPoints = trail.track.points;
    if (tPoints.length < 2) continue;
    const tCum = cumulativeDistances(tPoints);
    for (const crux of trail.cruxPoints ?? []) {
      const latLon = pointAtDistance(tPoints, tCum, crux.distanceMeters);
      if (!latLon) continue;
      const proj = nearestPointOnTrack(runPoints, latLon.lat, latLon.lon, runCum);
      out.push({
        distanceMeters: proj.alongTrackMeters,
        note: crux.note ?? null,
        elevationMeters: crux.elevationMeters,
      });
    }
  }

  out.sort((a, b) => a.distanceMeters - b.distanceMeters);
  return out;
}

/**
 * Wave 14ah: Difficulty-Liste in Reihenfolge der Trails im Run. Wird im
 * Detail-Sheet als "DH Run · Intermediate · Expert · Easy" angezeigt, wenn
 * der Run Trails mit unterschiedlichen Schwierigkeiten kombiniert.
 *
 * Trails ohne Eintrag in `trailsById` (verschoben/gelöscht) werden ignoriert
 * statt mit "unknown" zu erscheinen — das wäre für den User verwirrend, und
 * ein Run sollte vom Backend nie auf einen toten Trail zeigen.
 */
export function getRunSegmentDifficulties(
  run: CachedRun,
  trailsById: Map<string, CachedTrail>,
): Difficulty[] {
  const out: Difficulty[] = [];
  for (const id of run.trailIds) {
    const t = trailsById.get(id);
    if (t) out.push(t.difficulty);
  }
  return out;
}

/**
 * Wave 14ah: Wenn ein Run mehrere Trails mit unterschiedlichen Schwierigkeiten
 * kombiniert (typisch: blau → schwarz → blau), sollen die Map-Linien-Farben
 * SEGMENTWEISE wechseln. Diese Funktion zerschneidet die Run-GPX an den
 * Stellen wo (im Run-Verlauf) der nächste Trail beginnt.
 *
 * Algorithmus:
 *   1. Für jeden Trail in run.trailIds projizieren wir Trail-Start und Trail-
 *      Ende auf die Run-GPX (nearestPointOnTrack → alongTrackMeters).
 *   2. Aus den Projektions-Distanzen leiten wir Schnitt-Punkte ab. Da Trails
 *      sich auf der Run-GPX überschneiden können (z. B. exakte Übergangs-
 *      punkte) und die Reihenfolge in trailIds bereits stimmt, sortieren wir
 *      die Schnitte nicht nach Distanz — wir verlassen uns drauf, dass der
 *      Run-Recorder die Trails in der Reihenfolge ihrer trailIds gefahren ist.
 *   3. Für N Trails entstehen N Segmente: [0..d1], [d1..d2], ..., [d(N-1)..L].
 *      d_i ist der Mittelpunkt zwischen "Ende von Trail i-1" und "Start von
 *      Trail i" auf der Run-GPX — das absorbiert kleine Lücken/Überlappungen
 *      sauber.
 *   4. Jedes Segment kriegt difficulty + colorHex vom zugehörigen Trail.
 *      Trails ohne Cache-Eintrag werden ÜBERSPRUNGEN (s. o.) — ihre
 *      Distanz-Range fällt dann dem Nachbar-Segment zu.
 *
 * Robustheit-Notizen:
 *   - Wenn der Run nur 1 Trail enthält, geben wir 1 Segment = ganze Run-GPX
 *     zurück (kein nutzloses Splitting).
 *   - Wenn nach dem Filtern 0 Trails übrig sind (alle gelöscht?), Fallback:
 *     1 monochromes Segment mit der Run-eigenen Difficulty.
 *   - Endpunkt-Projektionen wirken auch dann robust, wenn der Trail nicht
 *     EXAKT entlang der Run-GPX liegt (kleine Detours, Haltestopps): wir
 *     nehmen den nächsten Run-Punkt als Anker.
 */
export interface RunSegment {
  /** Eindeutige Pseudo-Trail-ID für die Map: `run:<runId>:seg:<i>`. */
  id: string;
  /** Difficulty vom zugehörigen Original-Trail (oder Run-Difficulty als Fallback). */
  difficulty: Difficulty;
  /** Optional: explizite Farb-Override des Original-Trails. */
  colorHex: string | null;
  /** Sub-Track der Run-GPX, der zu diesem Segment gehört. */
  track: GpxTrack;
}

export function sliceRunIntoSegments(
  run: CachedRun,
  trailsById: Map<string, CachedTrail>,
): RunSegment[] {
  const runPoints = run.track.points;
  if (runPoints.length < 2) {
    // Track zu klein zum Splitten — eine Mini-Linie kommt eh nicht durch
    // den Cache, aber wir geben safe Default zurück.
    return [
      {
        id: `${RUN_ID_PREFIX}${run.id}:seg:0`,
        difficulty: run.difficulty,
        colorHex: null,
        track: run.track,
      },
    ];
  }

  // Cache cumulative-Distances einmalig — wir indexen mehrfach durch.
  const runCum = cumulativeDistances(runPoints);
  const totalLen = runCum[runCum.length - 1] ?? 0;

  // Für jeden bekannten (im Cache vorhandenen) Trail im Run: Start- und
  // End-Distanz auf der Run-GPX.
  type TrailRange = {
    trail: CachedTrail;
    startMeters: number;
    endMeters: number;
  };
  const ranges: TrailRange[] = [];
  for (const id of run.trailIds) {
    const trail = trailsById.get(id);
    if (!trail) continue;
    const tPoints = trail.track.points;
    if (tPoints.length < 2) continue;
    const startPoint = tPoints[0]!;
    const endPoint = tPoints[tPoints.length - 1]!;
    const startProj = nearestPointOnTrack(runPoints, startPoint.lat, startPoint.lon, runCum);
    const endProj = nearestPointOnTrack(runPoints, endPoint.lat, endPoint.lon, runCum);
    ranges.push({
      trail,
      startMeters: startProj.alongTrackMeters,
      endMeters: endProj.alongTrackMeters,
    });
  }

  if (ranges.length === 0) {
    // Komplett-Fallback: kein Trail im Cache → 1 monochromes Segment in
    // Run-Difficulty. Sieht visuell genauso aus wie vor Wave 14ah.
    return [
      {
        id: `${RUN_ID_PREFIX}${run.id}:seg:0`,
        difficulty: run.difficulty,
        colorHex: null,
        track: run.track,
      },
    ];
  }

  // Schnittpunkte: für i in 1..N-1 nimm den Mittelpunkt zwischen
  // ranges[i-1].endMeters und ranges[i].startMeters. Falls die Reihenfolge
  // an der Übergangsstelle gegen die Distanz läuft (kann passieren wenn der
  // User auf einer 8-förmigen Strecke fährt), klemmen wir auf [0,totalLen]
  // und sortieren am Ende, damit die Slicing-Logik unten valid bleibt.
  const cuts: number[] = [0];
  for (let i = 1; i < ranges.length; i++) {
    const prevEnd = ranges[i - 1]!.endMeters;
    const nextStart = ranges[i]!.startMeters;
    let mid = (prevEnd + nextStart) / 2;
    if (mid < 0) mid = 0;
    if (mid > totalLen) mid = totalLen;
    cuts.push(mid);
  }
  cuts.push(totalLen);

  // Defensiv: monoton steigend machen — sonst kollabieren Segmente bzw.
  // die Slicing-Indizes laufen rückwärts.
  for (let i = 1; i < cuts.length; i++) {
    if (cuts[i]! < cuts[i - 1]!) cuts[i] = cuts[i - 1]!;
  }

  const segments: RunSegment[] = [];
  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i]!;
    const a = cuts[i]!;
    const b = cuts[i + 1]!;
    const subPoints = sliceTrackByDistance(runPoints, runCum, a, b);
    if (subPoints.length < 2) continue; // Mikro-Segmente skippen
    segments.push({
      id: `${RUN_ID_PREFIX}${run.id}:seg:${i}`,
      difficulty: range.trail.difficulty,
      colorHex: range.trail.colorHex ?? null,
      track: { points: subPoints },
    });
  }

  if (segments.length === 0) {
    // Falls alles auf Mikro-Segmente kollabierte (extrem unwahrscheinlich) —
    // Fallback wie oben.
    return [
      {
        id: `${RUN_ID_PREFIX}${run.id}:seg:0`,
        difficulty: ranges[0]!.trail.difficulty,
        colorHex: ranges[0]!.trail.colorHex ?? null,
        track: run.track,
      },
    ];
  }

  return segments;
}

/**
 * Schneidet einen Track auf das Distanz-Intervall [startMeters, endMeters]
 * zurecht. Endpunkte werden interpoliert, damit benachbarte Segmente exakt
 * ohne Lücke/Überlappung aneinanderstoßen.
 */
function sliceTrackByDistance(
  points: GpxPoint[],
  cum: number[],
  startMeters: number,
  endMeters: number,
): GpxPoint[] {
  if (points.length === 0) return [];
  const total = cum[cum.length - 1] ?? 0;
  const a = Math.max(0, Math.min(startMeters, total));
  const b = Math.max(a, Math.min(endMeters, total));
  if (b - a < 0.5) return []; // <50cm = effektiv leer

  const out: GpxPoint[] = [];

  // Start-Punkt: interpoliert, falls a nicht exakt auf einem Vertex liegt.
  out.push(interpolatePointAt(points, cum, a));

  // Innere Vertices: alle die strikt zwischen a und b liegen.
  for (let i = 0; i < points.length; i++) {
    const d = cum[i] ?? 0;
    if (d > a + 1e-3 && d < b - 1e-3) out.push(points[i]!);
  }

  // End-Punkt: ebenso interpoliert.
  out.push(interpolatePointAt(points, cum, b));

  return out;
}

function interpolatePointAt(points: GpxPoint[], cum: number[], distanceMeters: number): GpxPoint {
  if (distanceMeters <= 0) return points[0]!;
  const total = cum[cum.length - 1] ?? 0;
  if (distanceMeters >= total) return points[points.length - 1]!;
  // Linearer Scan reicht — Track-Größen sind moderat.
  let i = 0;
  while (i < cum.length - 1 && (cum[i + 1] ?? 0) < distanceMeters) i++;
  const a = points[i]!;
  const b = points[Math.min(i + 1, points.length - 1)]!;
  const dStart = cum[i] ?? 0;
  const dEnd = cum[Math.min(i + 1, cum.length - 1)] ?? dStart;
  const segLen = dEnd - dStart;
  const t = segLen > 0 ? (distanceMeters - dStart) / segLen : 0;
  // Elevation interpolieren wenn beide Endpunkte sie haben — sonst
  // weglassen (GpxPoint.ele ist optional), damit elevationStats() nicht
  // plötzlich falsche Werte rechnet.
  const out: GpxPoint = {
    lat: a.lat + (b.lat - a.lat) * t,
    lon: a.lon + (b.lon - a.lon) * t,
  };
  if (a.ele != null && b.ele != null) out.ele = a.ele + (b.ele - a.ele) * t;
  return out;
}

/**
 * Baut aus einer CachedRun einen CachedTrail, der von der MapPage komplett
 * wie ein Trail behandelt werden kann.
 */
export function runAsTrail(
  run: CachedRun,
  trailsById: Map<string, CachedTrail>,
): CachedTrail {
  const stats = {
    lengthMeters: trackLength(run.track.points),
    // Wave 14bk: Deadband gegen GPS-Noise (s. trails.ts). Run-GPX kommt
    // aus denselben Aufzeichnungsquellen wie Trail-GPX, selbes Noise-Profil.
    ...elevationStats(run.track.points, { deadbandMeters: 2 }),
  };
  return {
    id: `${RUN_ID_PREFIX}${run.id}`,
    name: run.name,
    difficulty: run.difficulty,
    // Eigenes trailType-Label — wird im Detail-Sheet als "DH Run · intermediate"
    // angezeigt. Keine Logik-Sonderung darauf im Code.
    trailType: 'DH Run',
    colorHex: null,
    description: run.description ?? null,
    photos: [],
    gpxFilename: run.gpxFilename,
    track: run.track,
    cruxPoints: aggregateCruxesOntoRun(run, trailsById),
    stats,
    cachedAt: run.cachedAt,
  };
}

/**
 * Wave 14dw: Baut ElevationSegment[] für die RunDetailPage — selbes Format
 * wie `routeSegmentsToElevationSegments` für Custom Tours, sodass die
 * ElevationProfile-Komponente die Profil-Linie pro Trail einfärben kann.
 *
 * Wir benutzen dieselbe Range-Berechnung wie `sliceRunIntoSegments`:
 * Trail-Start- und End-Punkte auf die Run-GPX projizieren, Mittelpunkte
 * zwischen aufeinanderfolgenden Trails als Schnittlinien. Die Segmente
 * haben dann fromMeters/toMeters relativ zur Run-Cum-Länge (gleiches
 * Koordinatensystem wie `buildElevationDataFromGpx`).
 *
 * Trails ohne Cache-Eintrag werden übersprungen — ihre Distanz-Range
 * fällt dem Nachbar-Segment zu, sodass das Profil nahtlos bleibt.
 *
 * Wenn 0 Trails verfügbar sind, geben wir [] zurück → ElevationProfile
 * rendert monochrom wie vorher.
 */
export function buildRunElevationSegments(
  run: CachedRun,
  trailsById: Map<string, CachedTrail>,
): ElevationSegment[] {
  const runPoints = run.track.points;
  if (runPoints.length < 2) return [];

  const runCum = cumulativeDistances(runPoints);
  const totalLen = runCum[runCum.length - 1] ?? 0;
  if (totalLen <= 0) return [];

  type TrailRange = {
    trail: CachedTrail;
    startMeters: number;
    endMeters: number;
  };
  const ranges: TrailRange[] = [];
  for (const id of run.trailIds) {
    const trail = trailsById.get(id);
    if (!trail) continue;
    const tPoints = trail.track.points;
    if (tPoints.length < 2) continue;
    const startPoint = tPoints[0]!;
    const endPoint = tPoints[tPoints.length - 1]!;
    const startProj = nearestPointOnTrack(
      runPoints,
      startPoint.lat,
      startPoint.lon,
      runCum,
    );
    const endProj = nearestPointOnTrack(
      runPoints,
      endPoint.lat,
      endPoint.lon,
      runCum,
    );
    ranges.push({
      trail,
      startMeters: startProj.alongTrackMeters,
      endMeters: endProj.alongTrackMeters,
    });
  }
  if (ranges.length === 0) return [];

  // Cuts wie in sliceRunIntoSegments: Mittelpunkte zwischen Trail-Übergängen.
  const cuts: number[] = [0];
  for (let i = 1; i < ranges.length; i++) {
    const prevEnd = ranges[i - 1]!.endMeters;
    const nextStart = ranges[i]!.startMeters;
    let mid = (prevEnd + nextStart) / 2;
    if (mid < 0) mid = 0;
    if (mid > totalLen) mid = totalLen;
    cuts.push(mid);
  }
  cuts.push(totalLen);
  for (let i = 1; i < cuts.length; i++) {
    if (cuts[i]! < cuts[i - 1]!) cuts[i] = cuts[i - 1]!;
  }

  const out: ElevationSegment[] = [];
  for (let i = 0; i < ranges.length; i++) {
    const r = ranges[i]!;
    const a = cuts[i]!;
    const b = cuts[i + 1]!;
    if (b - a < 1) continue;
    out.push({
      fromMeters: a,
      toMeters: b,
      color: r.trail.colorHex || DIFFICULTY_COLOR[r.trail.difficulty],
      trailId: r.trail.id,
      trailName: r.trail.name,
      kind: 'trail',
      difficulty: r.trail.difficulty,
    });
  }
  return out;
}
